import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useIntl } from "gatsby-plugin-intl";
import { useStaticQuery, graphql } from "gatsby";
import get from "lodash/get";
import Slider from "react-slick";

import VariantCard from "./variantCard";
import KCArrowControls from "Components/common/kcArrowControls";
import KCButton from "Components/KCButton";
import { getVariantFromCurStyle } from "../../utils";

const StyledWrapper = styled.div`
  color: ${(props) => props.theme.color[props.textColor]};
  background-color: ${(props) =>
    props.theme.color[props.backgroundColor.color]};
  padding: 2rem 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
`;

const StyledControls = styled.div`
  display: flex;
  align-items: flex-end;
  width: 100%;
  padding: 0 1rem;
`;

const StyledLeftControl = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledTitle = styled.div`
  font-size: 2rem;
  font-weight: 600;
  margin-top: 1rem;
  flex: 1;
`;

const StyledNavWrapper = styled.div`
  display: flex;
  margin-top: 0.6rem;
  & > *:not(:first-child) {
    margin-left: 0.4rem;
  }
`;

const StyledProductCarousel = styled(Slider)`
  width: 100%;
  margin-top: 2rem;
  & .slick-list {
    padding: 0 20% 0 0;
  }
`;

const StyledVariantWrapper = styled.div`
  margin-right: 0.5rem;
  width: calc(100% - 0.5rem) !important;
  outline: none;
  &:first-of-type {
    margin-left: 1rem;
  }
`;

const ScrollSettings = {
  slidesToShow: 4.5,
  speed: 500,
  infinite: false,
  arrows: false,
  touchMove: true,
  accessibility: true,
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1.5,
      },
    },
  ],
};

export default function CarouselProductModal({
  title,
  productLists,
  textColor,
  backgroundColor,
}) {
  const [listIndex, setListIndex] = useState(0);
  const { allShopifyProductVariant, strapi } = useStaticQuery(graphql`
    {
      allShopifyProductVariant {
        edges {
          node {
            price
            presentmentPrices {
              price {
                amount
                currencyCode
              }
            }
            shopifyId
            sku
            selectedOptions {
              name
              value
            }
            title
          }
        }
      }
      strapi {
        spectacleStyles {
          color {
            name
          }
          lens {
            name
          }
          design {
            name
            kcID
          }
          thumbnail {
            url
            imageFile {
              childImageSharp {
                gatsbyImageData(width: 500, layout: CONSTRAINED)
              }
            }
          }
          kcID
        }
      }
    }
  `);
  const intl = useIntl();
  const getSpecStyle = (kcID) => {
    return (strapi?.spectacleStyles || []).find((style) => style.kcID === kcID);
  };

  const [slideIndex, setSlideIndex] = useState(0);
  const scrollWrapperRef = useRef(null);
  const handleNext = () => {
    if (typeof window !== "undefined") {
      let pageSize = window.innerWidth > 600 ? 3 : 1;
      scrollWrapperRef.current.slickGoTo(slideIndex + pageSize);
    }
  };

  const handlePrevious = () => {
    if (typeof window !== "undefined") {
      let pageSize = window.innerWidth > 600 ? 3 : 1;
      scrollWrapperRef.current.slickGoTo(slideIndex - pageSize);
    }
  };
  return (
    <StyledWrapper backgroundColor={backgroundColor} textColor={textColor}>
      <StyledControls>
        <StyledLeftControl>
          <StyledTitle>{title[intl.locale]}</StyledTitle>
          {productLists.length > 1 && (
            <StyledNavWrapper>
              {productLists.map((style, i) => (
                <KCButton
                  key={`grid-productNavButton-${i}`}
                  handleClick={() => setListIndex(i)}
                  size="small"
                  buttonType={i === listIndex ? "primary" : "default"}
                  label={get(style, `title[${intl.locale}]`, "")}
                />
              ))}
            </StyledNavWrapper>
          )}
        </StyledLeftControl>
        <KCArrowControls
          slideIndex={slideIndex}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          totalSlides={productLists[listIndex].styles.length - 3}
        />
      </StyledControls>
      <StyledProductCarousel
        ref={scrollWrapperRef}
        {...ScrollSettings}
        beforeChange={(oldI, newI) => {
          setSlideIndex(newI);
        }}
      >
        {productLists[listIndex].styles.map(({ style }) => {
          return (
            <StyledVariantWrapper key={`variantCard-${style.kcID}`}>
              <VariantCard
                style={getSpecStyle(style.kcID)}
                shopifyVariant={getVariantFromCurStyle(
                  style.kcID,
                  getSpecStyle(style.kcID),
                  "M",
                  allShopifyProductVariant
                )}
                textColor={textColor}
              />
            </StyledVariantWrapper>
          );
        })}
      </StyledProductCarousel>
    </StyledWrapper>
  );
}

export const query = graphql`
  fragment carouselProductModal on STRAPI_ComponentProductCarouselProduct {
    __typename
    id
    title {
      en
      es
      it
    }
    productLists {
      title {
        en
        es
        it
      }
      styles {
        style {
          kcID
        }
      }
    }
    textColor {
      color
    }
    backgroundColor {
      color
    }
  }
`;
